import { FC, useCallback } from 'react';
import { Formik } from 'formik';
import { useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import General from 'components/PackageEditTransportGeneral';
import Services from 'components/PackageEditTransportServices';
import Flights from 'components/PackageEditTransportFlights';

import { textFieldValue, useErrorHandler } from 'lib';
import { useCurrentUser } from 'components/CurrentUserProvider';
import { makeOption, Option } from 'components/AirportSelect';
import { SelectValue } from 'components/Select';

import {
  PackageEditTransport_package$key,
  TransportType,
} from '__generated__/PackageEditTransport_package.graphql';
import { PackageEditTransportMutation } from '__generated__/PackageEditTransportMutation.graphql';
import { PackageEditTransport_data$key } from '__generated__/PackageEditTransport_data.graphql';

type FormValues = {
  transportType: TransportType | null;
  externalFlights: boolean;
  transportName: string;
  transferPrice: string;
  services: readonly SelectValue[];
  defaultDepartureAirport: Option | null;
  defaultArrivalAirport: Option | null;
};

type Props = {
  package: PackageEditTransport_package$key;
  data: PackageEditTransport_data$key;
};

const PackageEditTransport: FC<Props> = props => {
  const errorHandler = useErrorHandler();
  const { itemsPerPage } = useCurrentUser();

  const pack = useFragment(
    graphql`
      fragment PackageEditTransport_package on Package {
        id
        transportType
        externalFlights
        transportName
        transferPrice

        services {
          id
          name
        }

        defaultDepartureAirport {
          id
          name
          code
        }

        defaultArrivalAirport {
          id
          name
          code
        }

        ...PackageEditTransportFlights_package
      }
    `,
    props.package
  );

  const data = useFragment(
    graphql`
      fragment PackageEditTransport_data on Query {
        ...PackageEditTransportServices_data
        ...PackageEditTransportFlights_data
      }
    `,
    props.data
  );

  const [update, loading] = useMutation<PackageEditTransportMutation>(graphql`
    mutation PackageEditTransportMutation(
      $first: Int!
      $id: ID!
      $input: UpdatePackageInput!
    ) {
      updatePackage(id: $id, input: $input) {
        ...PackageEditTabs_package
        ...PackageSidebar_package
      }
    }
  `);

  const {
    id,
    transportType,
    externalFlights,
    transportName,
    transferPrice,
    services,
    defaultDepartureAirport,
    defaultArrivalAirport,
  } = pack;

  const initial: FormValues = {
    transportName: transportName ?? '',
    transportType,
    externalFlights: externalFlights ?? false,
    transferPrice: textFieldValue(transferPrice),
    services,
    defaultDepartureAirport: defaultDepartureAirport
      ? makeOption(defaultDepartureAirport)
      : null,
    defaultArrivalAirport: defaultArrivalAirport
      ? makeOption(defaultArrivalAirport)
      : null,
  };

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const {
        transportType,
        externalFlights,
        transportName,
        transferPrice,
        services,
        defaultDepartureAirport,
        defaultArrivalAirport,
      } = values;

      update({
        variables: {
          id,
          first: itemsPerPage,
          input: {
            transportType,
            externalFlights,
            transportName,
            transferPrice: Number(transferPrice),
            services: services.map(({ id }, position) => ({
              id,
              position,
            })),
            defaultDepartureAirport: defaultDepartureAirport && {
              id: defaultDepartureAirport.value,
            },
            defaultArrivalAirport: defaultArrivalAirport && {
              id: defaultArrivalAirport.value,
            },
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, itemsPerPage, update]
  );

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={loading}>
        <AccordionController id="packageTransport">
          <General />
          <Services data={data} />

          {pack.transportType === 'PLANE' && !pack.externalFlights && (
            <Flights package={pack} data={data} />
          )}
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default PackageEditTransport;
